import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Select from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';

import { api } from 'src/api/api-client.config';
import Iconify from '../components/iconify';
import OptionDialog from 'src/components/pickem-admin/OptionDialog';
import { OptionsTable } from 'src/components/pickem-admin/OptionsTable';
import AssetsDialog from 'src/components/pickem-admin/AssetsDialog';
import { QUESTIONS } from 'src/constants/pickem';

import OptionsOrderingTable from '../components/pickem-admin/OptionsOrderingTable';
import { LanguageTabs, TabPanel } from '../components/language-tabs';
import { visibleLanguages } from '../config/quill-text-editor-settings';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { OptionType, QuestionType } from '@/@types/api.types.ts';
import { useCreateQuestionMutation } from '@/api/pickem-questions/createQuestion.ts';
import { useUpdateQuestionMutation } from '@/api/pickem-questions/updateQuestion.ts';
import { LocalizedDateTimePicker } from 'src/components/form/LocalizedDateTimePicker';
import { ViewTypeEnum } from 'src/enums/Pickem.enums';

const defaultQuestionState = {
  title_en: '',
  description_en: '',
  title_fr: '',
  description_fr: '',
  title_sl: '',
  description_sl: '',
  title_es: '',
  description_es: '',
  title_lt: '',
  description_lt: '',
  title_rus: '',
  description_rus: '',
  title_sp: '',
  description_sp: '',
  title_de: '',
  description_de: '',
  title_ca: '',
  description_ca: '',
  options: [],
  points: 15,
  category: 'WINS',
  view_type: 'PLAYER_LIST',
  bonus_options: [
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
    { label: '', is_correct: false },
  ],
} as unknown as QuestionType;

export default function PickemQuestionPage() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState(defaultQuestionState);
  const [openOptionDialog, setOpenOptionDialog] = useState(false);
  const [editOption, setEditOption] = useState<OptionType | null>(null);
  const [editOptionIndex, setEditOptionIndex] = useState<number>(-1);
  const [assetsTarget, setAssetsTarget] = useState<string | null>(null);
  const [_questionTitleIndex, _setQuestionTitleIndex] = useState(0);
  // const groupedQuestionsList = groupedQuestions();
  const [isOrderingMode, setIsOrderingMode] = useState<boolean>(false);

  const { pickemId, questionId } = useAppSearchParams();

  const {
    data: pickem,
    isLoading: isLoadingGetPickem,
    refetch: refetchPickem,
  } = useGetPickem(pickemId);

  const { mutateAsync: createQuestion, isPending: isPendingCreateQuestion } =
    useCreateQuestionMutation();
  const { mutateAsync: updateQuestion, isPending: isPendingUpdateQuestion } =
    useUpdateQuestionMutation();

  useEffect(() => {
    if (!pickem || !questionId) return;

    const question = pickem.questions.find(
      (question) => question.question_id === questionId
    );
    if (!question) return;

    // add default bonus options if they don't exist
    if (!(question.bonus_options?.length < 12)) {
      const missOptionsCount = 12 - question.bonus_options?.length || 0;
      for (let i = 0; i < missOptionsCount; i++) {
        question.bonus_options.push({ label: '', is_correct: false });
      }
    }

    if (question) {
      setQuestion(question);
      const questionIndex = QUESTIONS.findIndex(
        (quest) => quest.titleEn === question.title_en
      );
      if (questionIndex === -1) {
        _setQuestionTitleIndex(0);
      } else {
        _setQuestionTitleIndex(questionIndex);
      }
    }
  }, [pickem, questionId]);

  const handleChange = (e: any) => {
    const { id, value } = e.target;

    setQuestion((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const deleteImage = (key: string) => {
    setQuestion((prevState) => ({
      ...prevState,
      [key]: null,
    }));
  };

  const reorderOptionsHandler = (options: OptionType[]) => {
    setQuestion({ ...question, options: options });
  };

  const handleChangeCategory = (event: any) => {
    let points = 1;
    switch (event.target.value) {
      case 'WINS':
        points = 15;
        break;
      case 'GOALS_AGAINTS':
        points = 10;
        break;
      case 'CLEAN_SHEAT':
        points = 20;
        break;
      default:
        points = 1;
    }
    setQuestion((prevState) => ({
      ...prevState,
      category: event.target.value,
      points,
    }));
  };

  const handleChangeBonusType = (event: any) => {
    setQuestion((prevState) => ({
      ...prevState,
      bonus_type: event.target.value,
    }));
  };

  const handleChangeViewType = (event: any) => {
    setQuestion((prevState) => ({
      ...prevState,
      view_type: event.target.value,
    }));
  };

  const handleQuestionStartChange = (newValue: any) => {
    setQuestion((prevState) => ({
      ...prevState,
      start_utc: newValue,
    }));
  };

  const handleChangeIsBonus = (e: any) => {
    setQuestion((prevState) => ({
      ...prevState,
      is_bonus: e.target.checked,
    }));
  };

  const handleChangeIsTieBreak = (e: any) => {
    setQuestion((prevState) => ({
      ...prevState,
      is_tiebreak: e.target.checked,
    }));
  };

  const handleSaveQuestion = () => {
    question.bonus_options = question.bonus_options.filter(
      (item) => item.label !== ''
    );
    if (!questionId) {
      return createQuestion({ pickemId, question }).then(() => {
        refetchPickem().then(() =>
          navigate(`/dashboard/pickem/edit?pickem_id=${pickemId}`)
        );
      });
    }

    updateQuestion({ pickemId, question }).then(() => window.location.reload());
  };

  const [value, setValue] = useState(0);

  const handleChangeTabs = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  // OPTIONS handlers
  const successOptionHandler = (option: OptionType) => {
    if (!question?.options) {
      question.options = [];
    }

    if (editOption === null) {
      question.options.push(option);
    }

    if (editOption !== null) {
      question.options[editOptionIndex] = option;
    }
    setQuestion({ ...question });
    setEditOption(null);
    setOpenOptionDialog(false);
  };

  const deleteOptionHandler = (optionIndex: number) => {
    let option = question.options[optionIndex];
    if (option?.option_id) {
      api
        .delete(
          `/admin/pickem-options/${pickemId}?option_id=${option.option_id}`
        )
        .then(() => {
          question.options.splice(optionIndex, 1);
          setQuestion({ ...question });
        });
    } else {
      question.options.splice(optionIndex, 1);
      setQuestion({ ...question });
    }
  };

  const editOptionHandler = (optionIndex: number) => {
    setEditOption({ ...question.options[optionIndex] });
    setEditOptionIndex(optionIndex);
    setOpenOptionDialog(true);
  };

  const closeOptionDialog = () => {
    setEditOption(null);
    setOpenOptionDialog(false);
  };

  const openAddOptionDialog = () => {
    setEditOption(null);
    setOpenOptionDialog(true);
  };

  const onChoseAsset = (assetUrl: string) => {
    setQuestion((prevState) => ({
      ...prevState,
      [String(assetsTarget)]: assetUrl,
    }));
    setAssetsTarget(null);
  };

  const onCloseAssetsDialog = () => {
    setAssetsTarget(null);
  };

  const updateOptions = () => {
    api
      .put(
        `/admin/pickem-questions/${pickemId}?question_id=${question.question_id}`,
        question
      )
      .then(() => {
        window.location.reload();
      })
      .catch((error) => console.log('Update questions ERROR', error));
  };

  // const questionMenuItems = () => {
  //   let menuItems = [];
  //   for (const groupTitle of Object.keys(groupedQuestionsList)) {
  //     menuItems.push(<ListSubheader>{groupTitle}</ListSubheader>);
  //     for (const question of groupedQuestionsList[groupTitle]) {
  //       menuItems.push(
  //         <MenuItem key={question.index} value={question.index}>
  //           {question.titleEn}
  //         </MenuItem>
  //       );
  //     }
  //   }
  //   return menuItems;
  // };

  const OptionsButtons = () => (
    <Box>
      {!isOrderingMode ? (
        <>
          <Button
            variant='outlined'
            startIcon={<Iconify icon='eva:toggle-left-outline' />}
            onClick={() => setIsOrderingMode(true)}
            sx={{ mr: 2 }}
          >
            Re-Ordering Mode
          </Button>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-outline' />}
            onClick={() => openAddOptionDialog()}
          >
            Add Option
          </Button>
        </>
      ) : (
        <>
          <Button
            variant='outlined'
            startIcon={<Iconify icon='eva:close-outline' />}
            onClick={() => setIsOrderingMode(false)}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant='outlined'
            startIcon={<Iconify icon='eva:save-outline' />}
            onClick={() => updateOptions()}
            sx={{ mr: 2 }}
          >
            Save Order
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Helmet>
        <title> New Question </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            {questionId === null ? 'New' : 'Edit'} Question
          </Typography>
          <Box>
            <Button
              variant='outlined'
              startIcon={<Iconify icon='eva:close-outline' />}
              onClick={() =>
                navigate(`/dashboard/pickem/edit?pickem_id=${pickemId}`)
              }
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:save-outline' />}
              onClick={() => handleSaveQuestion()}
              disabled={
                isPendingCreateQuestion ||
                isPendingUpdateQuestion ||
                question.view_type === null ||
                !question.title_en
              }
            >
              Save
            </Button>
          </Box>
        </Stack>

        <Card style={{ padding: 20 }}>
          <Typography variant='h5' component='div' sx={{ marginBottom: 2 }}>
            Settings
          </Typography>
          {isLoadingGetPickem ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id='category-select-label'>Сategory</InputLabel>
                    <Select
                      labelId='category-label'
                      id='category-select'
                      value={question.category}
                      label='Category'
                      onChange={handleChangeCategory}
                    >
                      <MenuItem value='WINS'>WINS</MenuItem>
                      <MenuItem value='SAVES'>SAVES</MenuItem>
                      <MenuItem value='GOALS_AGAINTS'>GOALS AGAINST</MenuItem>
                      <MenuItem value='CLEAN_SHEAT'>SHOUTOUT</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id='view-type-select-label'>
                      View Type
                    </InputLabel>
                    <Select
                      labelId='view-type-select-label'
                      id='view-type-select'
                      value={question.view_type}
                      label='View Type'
                      onChange={handleChangeViewType}
                    >
                      {Object.values(ViewTypeEnum).map((viewType) => (
                        <MenuItem key={viewType} value={viewType}>
                          {viewType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <LocalizedDateTimePicker
                    label='Start'
                    value={question.start_utc}
                    onChange={handleQuestionStartChange}
                  />
                </Grid>
                <>
                  <Grid item xs={1}>
                    <TextField
                      id='points'
                      label='Points'
                      fullWidth
                      value={question.points}
                      variant='standard'
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField
                      id='bonus_points'
                      label='Bonus Points'
                      fullWidth
                      value={question.bonus_points}
                      variant='standard'
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField
                      id='tiebreak_score'
                      label='TieBreak Score'
                      fullWidth
                      value={question.tiebreak_score}
                      variant='standard'
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={question.is_tiebreak ?? false}
                          onChange={handleChangeIsTieBreak}
                          id='is_tiebreak'
                        />
                      }
                      label='Is TieBreak'
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={question.is_bonus ?? false}
                          onChange={handleChangeIsBonus}
                          id='is_bonus'
                        />
                      }
                      label='Is Bonus'
                    />
                  </Grid>
                </>

                {question.is_bonus ? (
                  <>
                    <Grid item xs={6} style={{ textAlign: 'center' }}>
                      {question.bonus_image ? (
                        <>
                          <img
                            style={{
                              margin: '0 auto',
                              width: 100,
                              background: 'grey',
                            }}
                            alt='Remy Sharp'
                            src={question.bonus_image}
                          />
                          <Iconify
                            icon='eva:edit-outline'
                            sx={{ width: 20, height: 20, cursor: 'pointer' }}
                            onClick={() => setAssetsTarget('bonus_image')}
                          />
                          <Iconify
                            icon='eva:close-outline'
                            sx={{ width: 20, height: 20, cursor: 'pointer' }}
                            onClick={() => deleteImage('bonus_image')}
                          />
                        </>
                      ) : (
                        <Iconify
                          icon='eva:plus-circle-outline'
                          sx={{
                            width: 20,
                            height: 20,
                            cursor: 'pointer',
                            marginTop: 2,
                            marginBottom: 2,
                          }}
                          onClick={() => setAssetsTarget('bonus_image')}
                        />
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel id='bonus-type-select-label'>
                          Bonus Type
                        </InputLabel>
                        <Select
                          labelId='bonus-type-select-label'
                          id='bonus_type'
                          value={question.bonus_type}
                          label='Bonus Type'
                          onChange={handleChangeBonusType}
                        >
                          <MenuItem value='2X'>2X</MenuItem>
                          <MenuItem value='3X'>3X</MenuItem>
                          <MenuItem value='4X'>4X</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </>
          )}

          <Grid container xs={12} style={{ marginTop: 30 }}>
            <Grid item xs={4} style={{ textAlign: 'center' }}>
              <Box>Main Image</Box>
              {question?.main_image && question.main_image !== 'undefined' ? (
                <>
                  <img
                    style={{
                      margin: '0 auto',
                      width: 100,
                      background: 'white',
                    }}
                    alt='Remy Sharp'
                    src={question.main_image}
                  />
                  <Iconify
                    icon='eva:edit-outline'
                    sx={{ width: 20, height: 20, cursor: 'pointer' }}
                    onClick={() => setAssetsTarget('main_image')}
                  />
                  <Iconify
                    icon='eva:close-outline'
                    sx={{ width: 20, height: 20, cursor: 'pointer' }}
                    onClick={() => deleteImage('main_image')}
                  />
                </>
              ) : (
                <Iconify
                  icon='eva:plus-circle-outline'
                  sx={{
                    width: 20,
                    height: 20,
                    cursor: 'pointer',
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                  onClick={() => setAssetsTarget('main_image')}
                />
              )}
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'center' }}>
              <Box>Secondary Image</Box>
              {question?.secondary_image &&
              question.secondary_image !== 'undefined' ? (
                <>
                  <img
                    style={{
                      margin: '0 auto',
                      width: 100,
                      background: 'white',
                    }}
                    alt='Remy Sharp'
                    src={question.secondary_image}
                  />
                  <Iconify
                    icon='eva:edit-outline'
                    sx={{ width: 20, height: 20, cursor: 'pointer' }}
                    onClick={() => setAssetsTarget('secondary_image')}
                  />
                  <Iconify
                    icon='eva:close-outline'
                    sx={{ width: 20, height: 20, cursor: 'pointer' }}
                    onClick={() => deleteImage('secondary_image')}
                  />
                </>
              ) : (
                <Iconify
                  icon='eva:plus-circle-outline'
                  sx={{
                    width: 20,
                    height: 20,
                    cursor: 'pointer',
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                  onClick={() => setAssetsTarget('secondary_image')}
                />
              )}
            </Grid>
          </Grid>
        </Card>

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={0}
          mt={2}
        >
          <Typography variant='h4' gutterBottom></Typography>
          <OptionsButtons />
        </Stack>

        <Box sx={{ width: '100%' }}>
          <LanguageTabs value={value} onChange={handleChangeTabs} />
          {visibleLanguages.map((lang, index) => (
            <TabPanel key={lang.code} value={value} index={index}>
              <Card sx={{ padding: 4 }}>
                {isLoadingGetPickem ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center' }}
                    style={{ padding: 20 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/*<Grid item xs={12}>*/}
                    {/*  <FormControl fullWidth>*/}
                    {/*    <InputLabel id='question-titile-select-label'>*/}
                    {/*      Question Title*/}
                    {/*    </InputLabel>*/}
                    {/*    <Select*/}
                    {/*      labelId='question-titile-label'*/}
                    {/*      id='question-titile-select'*/}
                    {/*      value={questionTitleIndex}*/}
                    {/*      label='Question Title'*/}
                    {/*      onChange={handleChangeQuestionTitle}*/}
                    {/*      disabled={true}*/}
                    {/*    >*/}
                    {/*      {questionMenuItems().map((item) => item)}*/}
                    {/*    </Select>*/}
                    {/*  </FormControl>*/}
                    {/*</Grid>*/}

                    <TextField
                      id={`title_${lang.code}`}
                      label={`Title ${lang.code}`}
                      fullWidth
                      value={question[`title_${lang.code}`]}
                      variant='standard'
                      onChange={handleChange}
                    />

                    <TextField
                      id={`description_${lang.code}`}
                      label={`Description ${lang.code}`}
                      fullWidth
                      value={question[`description_${lang.code}`]}
                      variant='standard'
                      onChange={handleChange}
                    />
                    <TextField
                      id='question_type'
                      label='Question Type'
                      fullWidth
                      value={question.question_type ?? ''}
                      variant='standard'
                      onChange={handleChange}
                      disabled={true}
                    />
                  </>
                )}
              </Card>
              <Card sx={{ padding: 4, marginTop: 1 }}>
                {isLoadingGetPickem ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center' }}
                    style={{ padding: 20 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {isOrderingMode ? (
                      <OptionsOrderingTable
                        options={question.options}
                        reorderHandler={reorderOptionsHandler}
                      />
                    ) : (
                      <OptionsTable
                        lang={lang.code}
                        options={question.options}
                        deleteOptionHandler={deleteOptionHandler}
                        editOptionHandler={editOptionHandler}
                        question={question}
                      />
                    )}
                  </>
                )}
              </Card>
            </TabPanel>
          ))}
        </Box>
      </Container>

      <OptionDialog
        option={editOption}
        open={openOptionDialog}
        onSuccessHandler={successOptionHandler}
        onClose={closeOptionDialog}
      />

      {assetsTarget && (
        <AssetsDialog onSuccess={onChoseAsset} onClose={onCloseAssetsDialog} />
      )}
    </>
  );
}
